/**
 * Mock Stripe Checkout page — Sprint 2.
 * Real Stripe never serves us a frontend page; in Sprint 11 this whole file is deleted.
 *
 * Flow: SettingsBilling.onUpgrade → POST /api/billing/checkout-session
 *   → returns { url: "/billing/mock-checkout?session_id=mock_cs_…" }
 *   → frontend window.location to that URL → renders THIS page.
 *
 * On Pay: POST /api/billing/mock-confirm → loopback webhook → plan upgraded.
 * On Cancel: window.location back to /settings/billing.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Lock, CreditCard, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { billingApi } from "@/lib/api";

export default function MockCheckoutPage() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const sessionId = params.get("session_id") || "";

  const [paying, setPaying] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!sessionId) {
        if (!cancelled) setError("Missing session_id");
        return;
      }
      try {
        const p = await billingApi.plan();
        if (!cancelled) setInfo({ tiers: p?.tiers || [], current: p?.plan });
      } catch (e) {
        if (!cancelled) setError(e?.message || "Unable to load checkout");
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  const onPay = () => {
    setPaying(true);
    billingApi.mockConfirm(sessionId)
      .then(() => {
        window.location.href = "/settings/billing?upgraded=1";
      })
      .catch((err) => {
        toast.error(err?.message || "Payment failed");
        setPaying(false);
      });
  };

  const onCancel = () => navigate("/settings/billing");

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center max-w-md w-full" data-testid="mock-checkout-error">
          <div className="text-[15px] font-semibold text-slate-900 mb-2">Checkout unavailable</div>
          <p className="text-[12.5px] text-slate-600 mb-4">{error}</p>
          <Button size="sm" onClick={onCancel}>Back to billing</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center py-10 sm:py-16 px-4" data-testid="mock-checkout-page">
      <div className="w-full max-w-[460px]">
        {/* Stripe-style header */}
        <div className="flex items-center gap-2 mb-6 text-slate-600">
          <Lock size={14} />
          <span className="text-[11.5px] uppercase tracking-wider font-semibold">Secure Mock Checkout</span>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100">
            <div className="text-[11.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Sellyx subscription</div>
            <div className="text-[20px] font-semibold text-slate-900">Upgrade your plan</div>
            <div className="text-[12px] text-slate-500 mt-1 font-mono">Session: {sessionId.slice(0, 20)}…</div>
          </div>

          <div className="px-6 py-5 space-y-4">
            <div className="rounded-xl bg-indigo-50 ring-1 ring-indigo-200 px-4 py-3 flex items-center gap-3">
              <CreditCard size={16} className="text-indigo-600 shrink-0" />
              <div className="flex-1">
                <div className="text-[12.5px] font-semibold text-indigo-900">Mock card on file</div>
                <div className="text-[11px] text-indigo-700">Visa •••• 4242 · This is a Sprint 2 mock — no real charge.</div>
              </div>
            </div>

            <div className="space-y-2 text-[12px] text-slate-600">
              <div className="flex justify-between"><span>Plan</span><span className="font-semibold text-slate-900">See backend session metadata</span></div>
              <div className="flex justify-between"><span>Billed monthly</span><span className="font-semibold text-slate-900">via mock invoice</span></div>
              <div className="flex justify-between border-t border-slate-100 pt-2 mt-2"><span className="font-semibold text-slate-900">Due today</span><span className="font-semibold text-slate-900">$0.00 (mock)</span></div>
            </div>

            <Button
              className="w-full h-10 bg-indigo-600 hover:bg-indigo-700"
              onClick={onPay}
              disabled={paying || !info}
              data-testid="mock-checkout-pay"
            >
              {paying ? "Processing…" : "Pay & subscribe"}
            </Button>

            <button
              onClick={onCancel}
              className="w-full text-center text-[12px] text-slate-500 hover:text-slate-700 py-1"
              data-testid="mock-checkout-cancel"
            >
              ← Cancel and return to billing
            </button>
          </div>

          <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center gap-2 text-[10.5px] text-slate-500">
            <ShieldCheck size={11} className="text-slate-400" />
            <span>Mock Stripe — Sprint 2. Real Stripe lands in Sprint 11.</span>
          </div>
        </div>

        <div className="text-center mt-6 text-[10.5px] text-slate-400">
          Sellyx · Mock Checkout (no real payment processed)
        </div>
      </div>
    </div>
  );
}
